import { Colors } from "../../config/DesignConstants";
import { createRegularPolygon } from "../../core/util/PolygonUtils";
import { V, V2d } from "../../core/Vector";
import { BaseEnemy } from "./BaseEnemy";
import { SteeringBehaviors } from "./util/SteeringBehaviors";

//=============================================================================
// SPIRALER CONFIGURATION - All tweakable values in one place
//=============================================================================

const SPIRALER_CONFIG = {
  // Basic stats
  health: 2, // Double interceptor health (tougher)
  killPoints: 10, // Double interceptor points
  mass: 0.4, // Slightly heavier than interceptors

  // Visual
  color: Colors.GREEN,
  secondaryColor: Colors.DARK_GREEN,
  strokeWidth: 1,
  size: 5,
  sides: 6, // Hexagon shape

  // Movement
  maxSpeed: 80, // Slower than interceptors (120)
  approachForce: 100, // Force strength when following waypoints
  speedBoostForce: 50, // Force to maintain speed in kamikaze mode
  velocityDamping: -0.1, // Minimal damping for smooth movement

  // Waypoint behavior
  waypointReachDistance: 20, // Distance to consider waypoint "reached"
  kamikazeSpeedThreshold: 40, // Speed below which to boost in kamikaze mode
} as const;

//=============================================================================
// SPIRALER ENEMY CLASS - Waypoint follower that becomes kamikaze
//=============================================================================

export class SpiralersEnemy extends BaseEnemy {
  // Waypoint following state
  private waypoints?: V2d[];
  private currentWaypointIndex = 0;
  private isKamikazeMode = false;

  constructor(position: V2d, waypoints?: V2d[]) {
    super(position, {
      health: SPIRALER_CONFIG.health,
      killPoints: SPIRALER_CONFIG.killPoints,
      mass: SPIRALER_CONFIG.mass,
      visualConfig: {
        points: [
          ...createRegularPolygon(SPIRALER_CONFIG.sides, SPIRALER_CONFIG.size),
        ],
        color: SPIRALER_CONFIG.color,
        secondaryColor: SPIRALER_CONFIG.secondaryColor,
        strokeWidth: SPIRALER_CONFIG.strokeWidth,
      },
    });

    this.waypoints = waypoints;

    // Initialize with velocity toward first waypoint if available
    if (this.body && this.waypoints && this.waypoints.length > 0) {
      const toFirstWaypoint = this.waypoints[0]
        .sub(V(this.body.position))
        .normalize();
      this.body.velocity = toFirstWaypoint.imul(SPIRALER_CONFIG.maxSpeed * 0.5);
    }
  }

  //===========================================================================
  // MAIN BEHAVIOR - Simple waypoint following with kamikaze mode
  //===========================================================================

  onTick(): void {
    if (!this.body) return;

    this.applyVelocityDamping();

    if (this.isKamikazeMode) {
      this.handleKamikazeMode();
    } else {
      this.followWaypoints();
    }
  }

  //===========================================================================
  // WAYPOINT FOLLOWING - Clear and simple
  //===========================================================================

  private followWaypoints(): void {
    if (!this.waypoints || this.currentWaypointIndex >= this.waypoints.length) {
      // No waypoints or reached the end - switch to kamikaze mode
      this.isKamikazeMode = true;
      return;
    }

    const position = V(this.body!.position);
    const targetWaypoint = this.waypoints[this.currentWaypointIndex];
    const toWaypoint = targetWaypoint.sub(position);
    const distanceToWaypoint = toWaypoint.magnitude;

    // Check if we've reached the current waypoint
    if (distanceToWaypoint < SPIRALER_CONFIG.waypointReachDistance) {
      this.moveToNextWaypoint();
      return;
    }

    // Use seek behavior to move toward current waypoint
    const result = SteeringBehaviors.seek(
      this,
      targetWaypoint,
      SPIRALER_CONFIG.approachForce,
    );

    this.body!.applyForce([result.force.x, result.force.y]);
  }

  private moveToNextWaypoint(): void {
    this.currentWaypointIndex++;

    // Check if we've exhausted all waypoints
    if (this.currentWaypointIndex >= this.waypoints!.length) {
      this.isKamikazeMode = true;
    }
  }

  //===========================================================================
  // KAMIKAZE MODE - Continue current velocity toward center
  //===========================================================================

  private handleKamikazeMode(): void {
    // The spiral path should naturally lead to the center
    // Just maintain speed and continue current direction
    const velocity = V(this.body!.velocity);
    const currentSpeed = velocity.magnitude;

    // Boost speed if moving too slowly
    if (currentSpeed < SPIRALER_CONFIG.kamikazeSpeedThreshold) {
      const currentDirection =
        velocity.magnitude > 0.1 ? velocity.normalize() : V(1, 0);
      const speedBoost = currentDirection.mul(SPIRALER_CONFIG.speedBoostForce);
      this.body!.applyForce([speedBoost.x, speedBoost.y]);
    }
  }

  //===========================================================================
  // HELPER METHODS - Support functions
  //===========================================================================

  private applyVelocityDamping(): void {
    // Apply minimal velocity damping for smooth movement
    const velocity = V(this.body!.velocity);
    const dampingForce = velocity.mul(SPIRALER_CONFIG.velocityDamping);
    this.body!.applyForce([dampingForce.x, dampingForce.y]);
  }

  //===========================================================================
  // PUBLIC INTERFACE - For debugging or external control
  //===========================================================================

  /**
   * Check if this spiraler has reached kamikaze mode
   */
  isInKamikazeMode(): boolean {
    return this.isKamikazeMode;
  }

  /**
   * Get current waypoint progress (for debugging)
   */
  getWaypointProgress(): { current: number; total: number } {
    return {
      current: this.currentWaypointIndex,
      total: this.waypoints?.length || 0,
    };
  }
}
